import React, { useEffect, useRef, useState } from 'react';
import { X, ZoomIn, ZoomOut, RotateCw, Download, Maximize2 } from 'lucide-react';

interface Props {
  src: string;
  alt?: string;
  onClose: () => void;
  filename?: string;
}

/**
 * Lightbox inline : ouvre l'image dans un overlay plutôt qu'un nouvel
 * onglet. Zoom molette / boutons, drag pour panner, rotation, ESC pour
 * fermer. Utilisé pour les reçus dans DemandesActivation et partout où
 * on veut éviter un `<a target="_blank">`.
 */
export const ImageLightbox: React.FC<Props> = ({ src, alt, onClose, filename }) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+') setScale(s => Math.min(5, s + 0.25));
      else if (e.key === '-') setScale(s => Math.max(0.5, s - 0.25));
      else if (e.key === '0') { setScale(1); setPan({ x: 0, y: 0 }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setScale(s => Math.max(0.5, Math.min(5, s + (e.deltaY < 0 ? 0.15 : -0.15))));
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (scale <= 1) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: pan.x, baseY: pan.y };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.baseX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.baseY + (e.clientY - dragRef.current.startY),
    });
  };
  const onMouseUp = () => { dragRef.current = null; };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex flex-col"
      onClick={onClose}
    >
      {/* Barre d'outils */}
      <div
        className="flex items-center justify-between px-4 py-2 bg-black/60 text-white text-sm border-b border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="truncate text-gray-200">{filename || alt || 'Image'}</div>
        <div className="flex items-center gap-1">
          <button onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
            className="p-1.5 hover:bg-white/10 rounded" title="Dézoomer (-)">
            <ZoomOut size={16} />
          </button>
          <span className="px-2 text-xs tabular-nums w-14 text-center">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale(s => Math.min(5, s + 0.25))}
            className="p-1.5 hover:bg-white/10 rounded" title="Zoomer (+)">
            <ZoomIn size={16} />
          </button>
          <button onClick={() => { setScale(1); setPan({ x: 0, y: 0 }); setRotation(0); }}
            className="p-1.5 hover:bg-white/10 rounded" title="Ajuster (0)">
            <Maximize2 size={16} />
          </button>
          <button onClick={() => setRotation(r => (r + 90) % 360)}
            className="p-1.5 hover:bg-white/10 rounded" title="Rotation">
            <RotateCw size={16} />
          </button>
          <a href={src} download={filename || 'image'}
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 hover:bg-white/10 rounded" title="Télécharger">
            <Download size={16} />
          </a>
          <button onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded ml-1" title="Fermer (Esc)">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Zone image */}
      <div
        className="flex-1 overflow-hidden flex items-center justify-center select-none"
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onClick={(e) => e.stopPropagation()}
        style={{ cursor: scale > 1 ? (dragRef.current ? 'grabbing' : 'grab') : 'default' }}
      >
        <img
          src={src}
          alt={alt || ''}
          draggable={false}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale}) rotate(${rotation}deg)`,
            transition: dragRef.current ? 'none' : 'transform 0.15s ease-out',
            maxWidth: '95vw',
            maxHeight: '85vh',
          }}
          className="object-contain"
        />
      </div>
    </div>
  );
};
